/**
 * SmartInspect Playbook — Consultant Quick Guide
 * -----------------------------------------------------------------------------
 * A business-focused guide (not a technical manual) that helps a consultant or
 * new team member understand how the SmartInspect ROI works in under 10 minutes.
 * Matches the SmartEvaluate Playbook design language (white background, #025979
 * blue accents, Chivo headings, enterprise cards). Content + UI only.
 */
import { useState } from "react";
import {
  ClipboardCheck, Search, Coins, SlidersHorizontal, Calculator, Globe,
  FileText, CheckCircle2, ChevronDown, ArrowRight, Building2, Activity,
  Wallet, Smartphone, Target, Users, TrendingUp, ScanLine, Quote,
} from "lucide-react";

const BLUE = "#025979";

export default function SmartInspectPlaybookPage() {
  return (
    <div data-testid="smartinspect-playbook-page" className="max-w-5xl mx-auto pb-20">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-md border border-[#E5E7EB] bg-gradient-to-br from-[#F0F9FF] via-white to-white mb-8">
        <div className="absolute right-0 top-0 h-full w-40 bg-gradient-to-l from-[#E0F2FE]/70 to-transparent pointer-events-none" />
        <div className="relative px-7 py-8">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#025979] font-bold mb-2">
            PrimeS4 · SmartInspect · Quick Guide
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            SmartInspect Playbook
          </h1>
          <p className="text-[13px] text-[#475569] mt-2 leading-relaxed max-w-2xl">
            Everything a consultant needs to understand the SmartInspect ROI in under
            10 minutes — how inputs, regional pricing and assumptions turn into a
            clear, defensible business case.
          </p>
        </div>
      </div>

      {/* 1. Overview */}
      <Section n="1" icon={ClipboardCheck} title="Overview"
        subtitle="What SmartInspect is and why the ROI matters">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <InfoCard icon={ScanLine} title="What SmartInspect is"
            body="A mobile-first inspection solution that replaces paper checklists, manual photo handling and SAP re-entry with a structured, digital inspection process." />
          <InfoCard icon={Activity} title="Problems it solves"
            body="Slow paper inspections, duplicate data entry into SAP, lost photos and evidence, painful audits and claims, and no real-time visibility of inspection activity." />
          <InfoCard icon={Users} title="Typical customers"
            body="Warehouse, logistics and asset-intensive operators running high inspection volumes across multiple locations on SAP." />
          <InfoCard icon={Target} title="Purpose of the ROI calculator"
            body="Turn a customer's current manual process into a clear business case — annual savings, investment, payback and multi-year ROI — ready for an executive conversation." />
        </div>
        <ConsultantNote>
          The ROI calculator produces a <strong>directional</strong> business case the
          consultant validates and presents. It complements consulting judgement — it
          does not replace it.
        </ConsultantNote>
      </Section>

      {/* 2. ROI Flow */}
      <Section n="2" icon={Search} title="ROI Flow"
        subtitle="How a business case is built, end to end">
        <FlowStrip className="mb-5" testid="smartinspect-playbook-roi-flow" steps={[
          { icon: ClipboardCheck, label: "Customer Inputs" },
          { icon: Globe, label: "Regional Pricing" },
          { icon: SlidersHorizontal, label: "ROI Assumptions" },
          { icon: Calculator, label: "ROI Calculations" },
          { icon: FileText, label: "Executive Report" },
        ]} />
        <Timeline testid="smartinspect-playbook-flow-steps" steps={[
          ["Customer Inputs", "Capture the current manual process, its costs and the scale of the SmartInspect rollout."],
          ["Regional Pricing", "The selected country sets the display currency, package price and device cost for the customer's region."],
          ["ROI Assumptions", "Admin-managed financial assumptions and expected improvement percentages that shape the model."],
          ["ROI Calculations", "Derive the future SmartInspect process, the savings, the investment and the returns."],
          ["Executive Report", "A client-ready summary of the business case, finalized and exported to PDF."],
        ]} />
      </Section>

      {/* 3. Customer Inputs */}
      <Section n="3" icon={ClipboardCheck} title="Customer Inputs"
        subtitle="What the consultant captures — and how it drives the ROI">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CompareCard icon={Building2} accent={BLUE} title="Company Information"
            rows={[
              ["Captured", "Company, industry, country and preferred currency."],
              ["Drives the ROI", "Sets the pricing region, display currency and report branding."],
            ]} testid="smartinspect-inputs-company" />
          <CompareCard icon={Activity} accent="#0E7490" title="Current Operations"
            rows={[
              ["Captured", "Locations, inspections per day, operating days, and the time each manual activity takes."],
              ["Drives the ROI", "Determines annual inspection volume and the hours the current process consumes."],
            ]} testid="smartinspect-inputs-operations" />
          <CompareCard icon={Wallet} accent="#B45309" title="Current Costs & Effort"
            rows={[
              ["Captured", "Inspector hourly cost (for inspection, administration, photo & SAP process work), Back Office / Claims Team hourly cost (for claims evidence preparation only), paper, and storage & IT cost."],
              ["Drives the ROI", "Converts saved time and materials into money — the core of the savings."],
            ]} testid="smartinspect-inputs-costs" />
          <CompareCard icon={Smartphone} accent="#7C3AED" title="SmartInspect Solution"
            rows={[
              ["Captured", "The selected commercial package (and any add-ons)."],
              ["Drives the ROI", "Sets the subscription, add-ons and implementation. Existing customer devices are reused — no device purchase."],
            ]} testid="smartinspect-inputs-solution" />
        </div>
      </Section>

      {/* 4. ROI Assumptions */}
      <Section n="4" icon={SlidersHorizontal} title="ROI Assumptions"
        subtitle="The editable assumptions behind every calculation">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <MetricCard title="Analysis Period" body="Number of years the business case is modelled over (typically 3)." />
          <MetricCard title="Discount Rate" body="Rate used to discount future benefits when calculating NPV." />
          <MetricCard title="Inflation" body="Annual inflation applied to recurring costs and benefits." />
          <MetricCard title="Paper Cost per Inspection" body="Benchmark paper & printing cost per inspection, used for paper savings." />
          <MetricCard title="Scenario Multipliers" body="Conservative / Expected / Optimistic % applied to the results. 100% = no adjustment." />
          <MetricCard title="Expected Improvements" body="Paper, Claims and Audit benefit percentages — each with a Conservative / Expected / Optimistic value." />
        </div>
        <p className="text-[12.5px] text-[#374151] leading-relaxed mb-2.5">
          Inspection, Administration, Photo Handling and SAP / ERP times now come from fixed SmartInspect Process Benchmarks (below). Expected Improvements cover only the three benefit categories that remain % + scenario driven:
        </p>
        <div className="flex flex-wrap gap-2">
          {["Paper Usage", "Claims & Evidence Preparation"].map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <MetricCard title="Visual Inspection" body="SmartInspect benchmark: 3.0 minutes per inspection." />
          <MetricCard title="Photograph Capture" body="SmartInspect benchmark: 1.5 minutes per inspection." />
          <MetricCard title="Inspection Code Recording" body="SmartInspect benchmark: 0.5 minutes per inspection." />
          <MetricCard title="Administration & Reporting" body="SmartInspect benchmark: 0.2 minutes per inspection." />
        </div>
        <p className="text-[12.5px] text-[#374151] leading-relaxed mt-3">
          SmartInspect activity time = min(current, benchmark), so a customer's time is never increased and savings are never negative. Photo Management and SAP / ERP Entry have a 0-minute SmartInspect future state, so their full current time becomes the saving. SmartInspect Total Process Time is the sum of all clamped activity times.
        </p>
        <ConsultantNote className="mt-4">
          There are <strong>no hidden discount factors</strong>. The scenario multipliers
          are the only realization layer — there is no confidence factor and no
          Year-1 realization adjustment.
        </ConsultantNote>
      </Section>

      {/* 5. How ROI is Calculated */}
      <Section n="5" icon={Calculator} title="How ROI is Calculated"
        subtitle="From the current process to the return — in plain business terms">
        <FlowStrip className="mb-5" testid="smartinspect-playbook-calc-flow" steps={[
          { icon: ClipboardCheck, label: "Inspection Volume" },
          { icon: Activity, label: "Current Process" },
          { icon: Coins, label: "SmartInspect Savings" },
          { icon: Wallet, label: "Investment" },
          { icon: TrendingUp, label: "Business Case" },
        ]} />
        <Timeline testid="smartinspect-playbook-calc-steps" steps={[
          ["1. Annual Inspection Volume", "Locations × inspections per day × operating days per year."],
          ["2. Current Process", "The current cost and effort of inspection, administration, photo handling, SAP / ERP entry, paper, storage & IT, claims and audit work."],
          ["3. SmartInspect Savings", "Inspection Labour, Administration & Reporting, Photo Handling and SAP / ERP savings come from the fixed SmartInspect activity benchmarks (current time vs benchmark). Paper & Printing, Storage & IT and Claims & Evidence Preparation are calculated separately — Paper and Claims & Evidence Preparation using the selected Conservative / Expected / Optimistic assumptions. Claims & Evidence Preparation uses staff hours per claim (evidence retrieval, preparation and coordination) and is separate from per-inspection Administration & Reporting."],
          ["4. SmartInspect Investment", "Annual Subscription, recurring Add-ons, Implementation and any one-time Add-ons. Existing customer devices are reused, so they are not a SmartInspect investment."],
          ["5. Business Case", "The savings and the investment are turned into the headline numbers below."],
        ]} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5" data-testid="smartinspect-playbook-business-case">
          <MetricCard title="Annual Net Benefit" body="Annual Savings − Annual Recurring Cost." />
          <MetricCard title="First-Year Investment" body="Annual Subscription + recurring add-ons + implementation + one-time add-ons." />
          <MetricCard title="ROI" body="The first-year return measured against the first-year investment." />
          <MetricCard title="Payback" body="First-Year Investment ÷ Monthly Gross Direct Savings (annual gross savings ÷ 12) — the time to recover the full first-year investment." />
          <MetricCard title="Three-Year Net Benefit" body="The cumulative net benefit built up over three years." />
          <MetricCard title="NPV" body="The present-day value of the future net benefits." />
        </div>
        <Callout tone="blue" icon={SlidersHorizontal} className="mt-4">
          Regional benchmarks and defaults (device, paper and storage & IT costs) pre-fill the
          questionnaire and can be <strong>overridden with the customer's own values</strong> where allowed.
        </Callout>
        <ConsultantNote className="mt-3">
          <strong>Additional Business Benefits</strong> — PRIVA insights, carrier &amp; damage trends,
          better compliance and connected operations — are shown for context but are
          <strong> not monetised</strong> in the ROI.
        </ConsultantNote>
      </Section>

      {/* 6. Regional Pricing */}
      <Section n="6" icon={Globe} title="Regional Pricing"
        subtitle="How Countries & Currencies shape the numbers">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <InfoCard icon={Coins} title="Display Currency"
            body="The customer's country sets the currency shown across the ROI and Executive Report (e.g. AED for the UAE)." />
          <InfoCard icon={Wallet} title="Package Pricing"
            body="Each pricing region has its own set subscription and add-on prices, maintained directly in the admin." />
          <InfoCard icon={Smartphone} title="Existing Devices Reused"
            body="SmartInspect runs on the customer's existing devices — there is no device purchase in the SmartInspect investment. Regional device cost is only used as a benchmark for the current process." />
        </div>
        <Callout tone="blue" icon={Globe}>
          <strong>No currency conversion is performed.</strong> Each region has its own
          prices set directly in Countries & Currencies — a UAE customer sees the AED
          prices configured for the UAE region, not a converted USD figure.
        </Callout>
      </Section>

      {/* 7. Executive Report */}
      <Section n="7" icon={FileText} title="Executive Report"
        subtitle="From assessment to a delivered business case">
        <FlowStrip testid="smartinspect-playbook-report-flow" steps={[
          { icon: ClipboardCheck, label: "Assessment" },
          { icon: Search, label: "Review" },
          { icon: CheckCircle2, label: "Finalize" },
          { icon: FileText, label: "Executive Report" },
          { icon: FileText, label: "PDF" },
          { icon: ArrowRight, label: "Email" },
        ]} />
        <p className="text-[12.5px] text-[#374151] leading-relaxed mt-4">
          The consultant completes the <strong>Assessment</strong>, <strong>reviews</strong> the
          numbers with the customer, then <strong>finalizes</strong> the agreed version. The
          finalized assessment produces the <strong>Executive Report</strong>, which can be
          exported to <strong>PDF</strong> and <strong>emailed</strong> to the customer.
        </p>
      </Section>

      {/* 8. Key Things to Remember */}
      <Section n="8" icon={CheckCircle2} title="Key Things to Remember">
        <CheckList tone="green" items={[
          "Annual Inspection Volume = locations × inspections per day × operating days per year.",
          "Savings come from seven categories: Inspection Labour, Paper & Printing, Photo Handling, SAP / ERP Entry, Storage & IT, Claims Administration and Audit Preparation.",
          "The Conservative / Expected / Optimistic assumptions are applied consistently to every savings category.",
          "Pricing is regional and set directly — there is no currency conversion.",
          "Existing customer devices are reused — devices are never a SmartInspect investment.",
          "First-Year Investment = subscription + recurring add-ons + implementation + one-time add-ons; recurring cost comes from the selected package.",
          "PRIVA insights, trends, compliance and connected operations are Additional Business Benefits — not monetised in the ROI.",
          "Regional benchmarks pre-fill the questionnaire and can be overridden with the customer's values where allowed.",
        ]} />
      </Section>

      {/* Philosophy */}
      <section data-testid="smartinspect-playbook-philosophy"
        className="relative overflow-hidden rounded-md border border-[#025979]/25 bg-gradient-to-br from-[#0F2A38] to-[#025979] text-white px-8 py-9 mt-6">
        <Quote className="w-8 h-8 text-white/40 mb-3" />
        <p className="text-[15px] sm:text-[17px] leading-relaxed font-medium max-w-3xl" style={{ fontFamily: "Chivo, sans-serif" }}>
          SmartInspect turns a customer's real inspection process into a transparent
          business case — every number traces back to what the customer told you,
          the region they operate in, and the improvements they expect.
        </p>
        <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">SmartInspect Philosophy</p>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Building blocks (mirrors the SmartEvaluate Playbook)               */
/* ------------------------------------------------------------------ */
function Section({ n, icon: Icon, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={`smartinspect-playbook-section-${n}`}
      className="mb-6 bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`smartinspect-playbook-toggle-${n}`}
        className="w-full flex items-center gap-3 px-6 py-4 bg-[#F9FAFB] border-b border-[#E5E7EB] text-left hover:bg-[#F3F4F6] transition-colors">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#025979] text-white shrink-0">
          {Icon ? <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> : <span className="text-[11px] font-bold">{n}</span>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-[#025979]">{n}</span>
            <h2 className="text-[15px] font-bold text-[#0F172A] tracking-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
          </div>
          {subtitle && <p className="text-[11.5px] text-[#6B7280] mt-0.5 leading-snug">{subtitle}</p>}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#9CA3AF] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 py-5">{children}</div>}
    </section>
  );
}

function FlowStrip({ steps, className = "", testid }) {
  return (
    <div data-testid={testid} className={`flex flex-col sm:flex-row sm:items-stretch gap-2 ${className}`}>
      {steps.map((s, i) => (
        <div key={s.label} className="flex flex-col sm:flex-row sm:items-center sm:flex-1 gap-2">
          <div className="flex-1 flex items-center gap-3 rounded-sm border border-[#025979]/20 bg-gradient-to-br from-[#E0F2FE] to-[#F0F9FF] px-4 py-3">
            <s.icon className="w-4.5 h-4.5 text-[#025979] shrink-0" style={{ width: 18, height: 18 }} />
            <span className="text-[12.5px] font-semibold text-[#0F172A]">{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="hidden sm:block w-4 h-4 text-[#94A3B8] shrink-0 rotate-0" />
          )}
        </div>
      ))}
    </div>
  );
}

function Timeline({ steps, testid }) {
  return (
    <ol data-testid={testid} className="relative border-l-2 border-[#E0F2FE] ml-3 space-y-5">
      {steps.map(([title, desc]) => (
        <li key={title} className="relative pl-6">
          <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#025979] ring-4 ring-[#E0F2FE]" />
          <p className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
          <p className="text-[12px] text-[#475569] leading-relaxed mt-0.5">{desc}</p>
        </li>
      ))}
    </ol>
  );
}

function InfoCard({ icon: Icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-[#E5E7EB] bg-white p-4 hover:border-[#025979]/40 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-[#E0F2FE]">
          <Icon className="w-4 h-4 text-[#025979]" />
        </span>
        <p className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <p className="text-[12px] text-[#475569] leading-relaxed">{body}</p>
    </div>
  );
}

function CompareCard({ icon: Icon, accent, title, rows, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-[#E5E7EB] bg-white overflow-hidden hover:shadow-md transition-shadow">
      <div className="px-4 py-3 flex items-center gap-2.5 border-b border-[#E5E7EB]" style={{ background: accent + "0F" }}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: accent + "1A" }}>
          <Icon className="w-4.5 h-4.5" style={{ color: accent, width: 18, height: 18 }} />
        </span>
        <p className="text-[14px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <div className="px-4 py-3 space-y-3">
        {rows.map(([l, v]) => (
          <div key={l}>
            <p className="text-[9.5px] uppercase tracking-wider font-bold text-[#94A3B8]">{l}</p>
            <p className="text-[12px] text-[#374151] leading-snug mt-0.5">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ title, body }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-3">
      <p className="text-[12px] font-bold text-[#025979]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      <p className="text-[11px] text-[#475569] leading-relaxed mt-1">{body}</p>
    </div>
  );
}

function CheckList({ items, tone }) {
  const color = tone === "red" ? "#B91C1C" : "#065F46";
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2 text-[12.5px] text-[#1F2937] leading-relaxed">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function ConsultantNote({ children, className = "" }) {
  return (
    <div className={`rounded-sm border-l-4 border-[#025979] bg-[#F0F9FF] px-4 py-3 ${className}`}>
      <p className="text-[9.5px] uppercase tracking-wider font-bold text-[#025979] mb-1">Consultant Note</p>
      <p className="text-[12px] text-[#334155] leading-relaxed">{children}</p>
    </div>
  );
}

const CALLOUT_TONES = {
  amber: "bg-[#FFFBEB] border-[#FCD34D]/60 text-[#78350F]",
  blue: "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]",
};

function Callout({ tone = "blue", icon: Icon, children, className = "" }) {
  return (
    <div className={`flex items-start gap-2.5 rounded-sm border px-4 py-3 text-[12px] leading-relaxed ${CALLOUT_TONES[tone]} ${className}`}>
      {Icon && <Icon className="w-4 h-4 shrink-0 mt-0.5" />}
      <span>{children}</span>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 text-[11.5px] font-medium">
      {children}
    </span>
  );
}
